<script lang="ts">
  import {
    faCheck,
    faPaperPlane,
    faReply,
    faRotateLeft,
    faTrash,
    faXmark,
  } from '@fortawesome/free-solid-svg-icons';
  import type { CommentThread, DrawingComment } from '$lib/core/model';
  import { useComments } from '../comment-session.svelte';
  import type { EditorState } from '../editor-state.svelte';
  import Icon from './Icon.svelte';

  const { editor }: { editor: EditorState } = $props();
  const comments = useComments();

  /** Each open thread's reply text, by thread id. */
  let replies = $state<Record<string, string>>({});

  const when = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  const authorOf = ({ author }: DrawingComment) =>
    author.kind === 'ai' ? (author.name ?? 'AI agent') : author.name;

  /** Enter sends, as in chat apps; Shift+Enter starts a new line; composing never sends. */
  const sends = (event: KeyboardEvent) =>
    event.key === 'Enter' && !event.shiftKey && !event.isComposing;

  async function reply(thread: CommentThread) {
    if (await comments.reply(thread.id, replies[thread.id] ?? '')) replies[thread.id] = '';
  }
</script>

{#snippet comment(thread: CommentThread, each: DrawingComment)}
  <div class="comment" class:reply={each.id !== thread.comments[0]?.id}>
    <div class="byline">
      {#if each.text === null}
        <span class="deleted">Comment deleted</span>
      {:else}
        <span class="author">{authorOf(each)}</span>
        {#if each.author.kind === 'ai'}<span class="tag">AI</span>{/if}
        <time datetime={new Date(each.at).toISOString()}>{when.format(each.at)}</time>
      {/if}
      {#if comments.canDelete(each)}
        <button
          type="button"
          class="icon trash"
          aria-label="Delete comment by {authorOf(each)}"
          title="Delete this comment"
          onclick={() => comments.remove(thread, each)}
        >
          <Icon icon={faTrash} />
        </button>
      {/if}
    </div>
    {#if each.text !== null}<p class="text">{each.text}</p>{/if}
  </div>
{/snippet}

{#if comments.problem}<p class="problem" role="alert">{comments.problem}</p>{/if}

{#if editor.commentRegion}
  <form
    class="draft panel-item"
    aria-label="New comment"
    onsubmit={(event) => {
      event.preventDefault();
      void comments.post();
    }}
  >
    <textarea
      id="comment-draft"
      rows="3"
      placeholder="What should change in the box?"
      aria-label="New comment"
      bind:value={comments.draft}
      onkeydown={(event) => {
        if (sends(event)) {
          event.preventDefault();
          void comments.post();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          comments.cancel();
        }
      }}></textarea>
    <div class="actions">
      <button type="button" class="icon" title="Drop this box" onclick={() => comments.cancel()}>
        <Icon icon={faXmark} />Cancel
      </button>
      <button type="submit" class="icon" title="Post the comment">
        <Icon icon={faPaperPlane} />Post
      </button>
    </div>
  </form>
{:else if comments.ordered.length === 0}
  <p class="empty">
    Pick Comment, or press <kbd>C</kbd>, then drag a box around what the comment is about.
  </p>
{/if}

{#if comments.ordered.length > 0}
  <ul class="threads panel-list">
    {#each comments.ordered as thread (thread.id)}
      <li
        class="panel-item"
        class:closed={thread.closed}
        class:selected={comments.selectedId === thread.id}
        aria-label="Comment thread"
      >
        <div class="head">
          <button
            type="button"
            class="pick"
            aria-pressed={comments.selectedId === thread.id}
            title="Show the box this comment is about"
            onclick={() => comments.select(thread)}
          >
            View
          </button>
          {#if thread.closed}<span class="tag">Closed</span>{/if}
        </div>
        {#each thread.comments as each (each.id)}
          {@render comment(thread, each)}
        {/each}
        {#if !thread.closed}
          <textarea
            class="reply-text"
            rows="1"
            placeholder="Reply"
            aria-label="Reply"
            bind:value={() => replies[thread.id] ?? '', (text) => (replies[thread.id] = text)}
            onkeydown={(event) => {
              if (sends(event)) {
                event.preventDefault();
                void reply(thread);
              }
            }}></textarea>
        {/if}
        <div class="actions">
          {#if thread.closed}
            <button
              type="button"
              class="icon"
              title="Reopen this thread"
              onclick={() => comments.setClosed(thread.id, false)}
            >
              <Icon icon={faRotateLeft} />Reopen
            </button>
          {:else}
            <button
              type="button"
              class="icon"
              title="Mark this thread done"
              onclick={() => comments.setClosed(thread.id, true)}
            >
              <Icon icon={faCheck} />Close
            </button>
            <button type="button" class="icon" title="Post the reply" onclick={() => reply(thread)}>
              <Icon icon={faReply} />Reply
            </button>
          {/if}
        </div>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .empty {
    margin: 0;
    color: var(--color-text-muted);
    font-size: 0.8125rem;
  }

  .problem {
    margin: 0 0 var(--space-2);
    color: var(--color-danger);
    font-size: 0.75rem;
  }

  .draft {
    flex: none;
    margin-bottom: var(--space-2);
  }

  .threads {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  .closed {
    opacity: 0.7;
  }

  .selected {
    border-color: var(--color-accent);
  }

  .head {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--color-text-muted);
  }

  .pick {
    padding: 0;
    border: 0;
    background: none;
    color: var(--color-accent);
    font: inherit;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
  }

  .pick:hover {
    text-decoration: underline;
  }

  .comment {
    margin-top: 4px;
    font-size: 0.8125rem;
  }

  /* Replies sit indented under the comment they answer. */
  .comment.reply {
    margin-left: var(--space-3);
    padding-left: var(--space-2);
    border-left: 2px solid var(--color-border);
  }

  .byline {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--color-text-muted);
    font-size: 0.6875rem;
  }

  .author {
    color: var(--color-text);
    font-weight: 600;
  }

  .tag {
    padding: 0 4px;
    border: 1px solid var(--color-border);
    border-radius: 4px;
    font-size: 0.625rem;
  }

  .deleted {
    font-style: italic;
  }

  .text {
    margin: 2px 0 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    user-select: text;
  }

  textarea {
    box-sizing: border-box;
    width: 100%;
    margin-top: 6px;
    padding: 4px 6px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font: inherit;
    font-size: 0.8125rem;
    resize: vertical;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 4px;
  }

  button.icon {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: none;
    color: var(--color-text-muted);
    font: inherit;
    font-size: 0.75rem;
    cursor: pointer;
  }

  button.icon:hover {
    border-color: var(--color-border);
    color: var(--color-text);
  }

  .trash {
    margin-left: auto;
  }
</style>
